import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import {
  connectConsoleWebSocket,
  subscribeConsoleNotificationEvents,
} from '@/lib/console-websocket';
import {
  consoleNotificationKeys,
  fetchConsoleNotifications,
  fetchConsoleUnreadCount,
  markConsoleNotificationsRead,
} from '@/lib/notifications';
import { useAuthStore } from '@/stores/auth-store';
import { useChurchStore } from '@/stores/church-store';

export const useHasConsoleNotificationAccess = (): boolean => {
  const membership = useChurchStore((state) => state.membership);
  return membership != null && membership.systemRole !== 'congregant';
};

export const useConsoleUnreadCount = () => {
  const enabled = useHasConsoleNotificationAccess();

  return useQuery({
    queryKey: consoleNotificationKeys.unreadCount(),
    queryFn: fetchConsoleUnreadCount,
    enabled,
    select: (response) => response.unreadCount,
    staleTime: 30_000,
  });
};

export const useConsoleNotifications = (input: {
  page: number;
  pageSize: number;
  unreadOnly?: boolean;
}) => {
  const enabled = useHasConsoleNotificationAccess();

  return useQuery({
    queryKey: consoleNotificationKeys.list(input.page, input.pageSize, input.unreadOnly ?? false),
    queryFn: () => fetchConsoleNotifications(input),
    enabled,
  });
};

export const useMarkConsoleNotificationsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markConsoleNotificationsRead,
    onSuccess: (result) => {
      queryClient.setQueryData(consoleNotificationKeys.unreadCount(), {
        unreadCount: result.unreadCount,
      });
      void queryClient.invalidateQueries({ queryKey: consoleNotificationKeys.all });
    },
  });
};

/** Keeps the console notifications socket connected while staff are signed in. */
export const useConsoleNotificationRealtime = (): void => {
  const enabled = useHasConsoleNotificationAccess();
  const userId = useAuthStore((state) => state.user?.id);
  const churchId = useChurchStore((state) => state.church?.id);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled || !userId || !churchId) return;

    const unsubscribeEvents = subscribeConsoleNotificationEvents((payload) => {
      queryClient.setQueryData(consoleNotificationKeys.unreadCount(), {
        unreadCount: payload.unreadCount,
      });
      void queryClient.invalidateQueries({ queryKey: consoleNotificationKeys.all });
    });

    const disconnect = connectConsoleWebSocket({ churchId, userId });

    return () => {
      unsubscribeEvents();
      disconnect();
    };
  }, [churchId, enabled, queryClient, userId]);
};
